import { useState } from "react";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  isBefore,
  startOfDay,
  endOfDay,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type QuickDateFilterKey =
  | "all_todo"
  | "overdue"
  | "today"
  | "tomorrow"
  | "this_week"
  | "next_week"
  | "custom";

export interface DateRange {
  start: Date;
  end: Date;
}

interface AgendaDateQuickFilterProps {
  activeKey: QuickDateFilterKey;
  onSelectKey: (key: QuickDateFilterKey) => void;
  customRange: DateRange | null;
  onApplyCustomRange: (range: DateRange) => void;
  className?: string;
}

export function AgendaDateQuickFilter({
  activeKey,
  onSelectKey,
  customRange,
  onApplyCustomRange,
  className,
}: AgendaDateQuickFilterProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(
    () => customRange?.start || new Date(),
  );
  const [pendingStart, setPendingStart] = useState<Date | null>(
    () => customRange?.start || null,
  );
  const [pendingEnd, setPendingEnd] = useState<Date | null>(
    () => customRange?.end || null,
  );

  const leftMonth = startOfMonth(viewDate);
  const rightMonth = startOfMonth(addMonths(viewDate, 1));

  const handlePrevMonth = () => {
    setViewDate((d) => subMonths(d, 1));
  };

  const handleNextMonth = () => {
    setViewDate((d) => addMonths(d, 1));
  };

  const handleDayClick = (day: Date) => {
    if (!pendingStart || (pendingStart && pendingEnd)) {
      setPendingStart(day);
      setPendingEnd(null);
    } else if (pendingStart && !pendingEnd) {
      if (isBefore(day, pendingStart)) {
        setPendingStart(day);
        setPendingEnd(null);
      } else {
        setPendingEnd(day);
      }
    }
  };

  const handleApply = () => {
    if (pendingStart) {
      const finalEnd = pendingEnd || pendingStart;
      onApplyCustomRange({
        start: startOfDay(pendingStart),
        end: endOfDay(finalEnd),
      });
      onSelectKey("custom");
      setPopoverOpen(false);
    }
  };

  const handleCancel = () => {
    setPendingStart(customRange?.start || null);
    setPendingEnd(customRange?.end || null);
    setPopoverOpen(false);
  };

  const renderMonthCalendar = (monthDate: Date) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const days = eachDayOfInterval({ start: startDate, end: endDate });
    const weekDays = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

    return (
      <div className="flex-1 min-w-[220px]">
        {/* Month Header */}
        <div className="text-center font-semibold text-sm capitalize text-blue-600 dark:text-blue-400 py-1">
          {format(monthDate, "MMMM yyyy", { locale: ptBR })}
        </div>

        {/* Weekday labels */}
        <div className="grid grid-cols-7 text-center text-xs text-muted-foreground font-medium my-2">
          {weekDays.map((d) => (
            <div key={d} className="py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-y-1 text-center text-xs">
          {days.map((day) => {
            const isCurrentMonth = isSameMonth(day, monthDate);
            const isStart = pendingStart && isSameDay(day, pendingStart);
            const isEnd = pendingEnd && isSameDay(day, pendingEnd);
            const inRange =
              pendingStart &&
              pendingEnd &&
              isWithinInterval(day, { start: pendingStart, end: pendingEnd });

            return (
              <div
                key={day.toISOString()}
                className="flex justify-center p-0.5"
              >
                <button
                  type="button"
                  onClick={() => handleDayClick(day)}
                  disabled={!isCurrentMonth}
                  className={cn(
                    "w-7 h-7 rounded-md flex items-center justify-center transition-all cursor-pointer",
                    !isCurrentMonth && "opacity-0 pointer-events-none",
                    isCurrentMonth &&
                      !isStart &&
                      !isEnd &&
                      !inRange &&
                      "hover:bg-muted text-foreground",
                    inRange &&
                      !isStart &&
                      !isEnd &&
                      "bg-blue-100/70 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200 rounded-none",
                    (isStart || isEnd) &&
                      "bg-blue-500 text-white font-bold shadow-xs hover:bg-blue-600 rounded-md",
                  )}
                >
                  {format(day, "d")}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getCustomButtonLabel = () => {
    if (activeKey === "custom" && customRange) {
      return `${format(customRange.start, "dd/MM")} - ${format(customRange.end, "dd/MM")}`;
    }
    return "Selecionar período";
  };

  return (
    <div
      className={cn(
        "flex items-center gap-1 sm:gap-2 flex-wrap text-xs",
        className,
      )}
    >
      {/* Para fazer */}
      <button
        type="button"
        onClick={() => onSelectKey("all_todo")}
        className={cn(
          "px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer",
          activeKey === "all_todo"
            ? "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 font-semibold shadow-2xs"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
        )}
      >
        Para fazer
      </button>

      {/* Vencido */}
      <button
        type="button"
        onClick={() => onSelectKey("overdue")}
        className={cn(
          "px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer",
          activeKey === "overdue"
            ? "bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 font-semibold shadow-2xs"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
        )}
      >
        Vencido
      </button>

      {/* Hoje */}
      <button
        type="button"
        onClick={() => onSelectKey("today")}
        className={cn(
          "px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer",
          activeKey === "today"
            ? "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 font-semibold shadow-2xs"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
        )}
      >
        Hoje
      </button>

      {/* Amanhã */}
      <button
        type="button"
        onClick={() => onSelectKey("tomorrow")}
        className={cn(
          "px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer",
          activeKey === "tomorrow"
            ? "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 font-semibold shadow-2xs"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
        )}
      >
        Amanhã
      </button>

      {/* Esta semana */}
      <button
        type="button"
        onClick={() => onSelectKey("this_week")}
        className={cn(
          "px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer",
          activeKey === "this_week"
            ? "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 font-semibold shadow-2xs"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
        )}
      >
        Esta semana
      </button>

      {/* Próxima semana */}
      <button
        type="button"
        onClick={() => onSelectKey("next_week")}
        className={cn(
          "px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer",
          activeKey === "next_week"
            ? "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 font-semibold shadow-2xs"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
        )}
      >
        Próxima semana
      </button>

      {/* Selecionar período ▾ (Double month picker) */}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "px-3 py-1.5 rounded-lg font-medium transition-all flex items-center gap-1.5 cursor-pointer border",
              activeKey === "custom"
                ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800 font-semibold shadow-2xs"
                : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
          >
            <span>{getCustomButtonLabel()}</span>
            <ChevronDown className="w-3.5 h-3.5 opacity-70" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-4 sm:p-5 shadow-2xl rounded-2xl border border-border/80 bg-popover"
          align="start"
        >
          <div className="flex flex-col gap-4">
            {/* Calendar Controls & Dual Month View */}
            <div className="relative">
              {/* Prev / Next buttons */}
              <div className="absolute top-0.5 left-0 right-0 flex justify-between pointer-events-none z-10 px-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handlePrevMonth}
                  className="h-7 w-7 rounded-md pointer-events-auto shadow-2xs cursor-pointer"
                  title="Mês anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleNextMonth}
                  className="h-7 w-7 rounded-md pointer-events-auto shadow-2xs cursor-pointer"
                  title="Próximo mês"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              {/* Side by side months */}
              <div className="flex flex-col sm:flex-row gap-6 pt-1">
                {renderMonthCalendar(leftMonth)}
                <div className="hidden sm:block w-px bg-border/60" />
                {renderMonthCalendar(rightMonth)}
              </div>
            </div>

            {/* Bottom Actions: Cancelar & Aplicar (Green button) */}
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border/60">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="h-9 px-4 text-xs font-semibold rounded-lg cursor-pointer"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleApply}
                disabled={!pendingStart}
                className="h-9 px-5 text-xs font-bold rounded-lg bg-[#70a370] hover:bg-[#629462] text-white shadow-xs cursor-pointer transition-all disabled:opacity-50"
              >
                Aplicar
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
